import React, { useContext, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Dropdown from 'react-bootstrap/Dropdown';
import { styles, Icon, Switch } from 'yankees-react-utils';
import { useOktaAuth } from '@okta/okta-react';
import { useTranslation } from 'react-i18next';
import { store, actions } from '../store';
import usePlayerSelector from '../utils/usePlayerSelector';
import PlayerSelector from '../components/PlayerSelector';
import header from '../images/play_portal_icon.svg';
import FeedbackModal from './FeedbackModal';
import LanguageModal from './LanguageModal';
import { useOktaQuery, Login, PlayerSettings } from '../data';
import usePlayerSettings from '../utils/usePlayerSettings';

export const headerHeight = '50px';
export const headerPaddingLeftRight = '20px';

const StyledHeader = styled(Navbar)`
    flex-grow: 0;
    background-color: #254C75;
    color: ${styles.theme.fontColors.anakiwa};
    && {
        padding: 12px ${headerPaddingLeftRight};
        height: ${headerHeight};
        .h3 {
            margin-bottom: 0;
        }
    }
    .h3 {
        text-transform: uppercase;
        color: white;
        &:hover {
            text-decoration: none;
            color: inherit;
        }
    }
    .spacer {
        flex-grow: 1;
    }
    .nyy-icon {
        &:hover {
            cursor: pointer;
        }
    }
    .header-logo {
        max-height: 100%;
    }
    .dropdown-divider {
        margin: .375rem 1rem;
    }
    .dropdown-header { 
        padding: 0.25rem 1rem;
    }
    .subgroup-dropdown-item {
        padding-left: 2rem;
    }
    .settings-dropdown {
        font-size: 0.75rem;
        font-weight: bold;
    }
`;
const StyledContainer = styled(Container)`
    flex-grow: 1;
    overflow-y: auto;
    &&& {
        padding: ${styles.theme.margin.body};
    }
`;
const StyledFooter = styled.div`
    padding: 0.5em 1em;
    font-size: 0.9em;
    color: #555;

    @media (max-width: 1112px) {
        padding: 0.25em 1em;
        font-size: 0.7em;
    }
`;

const StyledGameScoreToggleWrapper = styled.span`
    margin-left: -2.5rem;
    label {
        height: 26px;
        width: 44px;
    }
    div {
        height: 26px;
        line-height: 0;
    }
    span::before {
        height: 20px;
        width: 20px;
        bottom: 3px;
        left: 4px;
    }
    input:checked + .toggle::before {
        transform: translateX(13px);
        left: 7px;
    }
`;

const SupportToggle = React.forwardRef(({ onClick }, ref) => (
    <div
        ref={ref}
        onClick={(e) => {
            e.preventDefault();
            onClick(e);
        }}
    >
        <Icon
            icon="cog"
            size="2em"
        />
    </div>
));
const SectionContentContainer = ({ children, ...rest }) => {
    const { t: translate } = useTranslation();
    const { dispatch, state: { user: loggedInUser } } = useContext(store);
    const userPlayerId = loggedInUser?.IsPlayer && loggedInUser.PlayerID;
    const { FullName, TeamID } = usePlayerSelector(true);
    const { IsMlb, IsPitcher, PlayerID } = usePlayerSelector();
    const { oktaAuth, authState: { accessToken } } = useOktaAuth();
    const history = useHistory();
    const { ShowGameScore } = usePlayerSettings();

    const isDeveloper = !!loggedInUser.Roles.find((r) => r.RoleName === 'developer');
    const { data: buildName } = useOktaQuery(['loginVersion'], (tr) => Login.getVersion(tr), { enabled: isDeveloper });

    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [showLanguageModal, setShowLanguageModal] = useState(false);
    const [isSavingShowGameScore, setIsSavingShowGameScore] = useState(false);

    const logout = async () => {
        dispatch({ type: actions.LOG_OUT });
        oktaAuth.signOut('/login');
    };
    const onPlayerSelect = (aliasPlayer) => {
        history.push('/');
        dispatch({ type: actions.SELECT_ALIAS, data: aliasPlayer });
    };
    const changeTeam = async () => {
        onPlayerSelect(null);
    };
    const updateShowGameScore = async () => {
        if (isSavingShowGameScore) return;
        try {
            setIsSavingShowGameScore(true);
            const response = await PlayerSettings.update(accessToken, {
                playerId: PlayerID,
                key: 'ShowGameScore',
                value: (!ShowGameScore).toString(),
            }, IsMlb, IsPitcher);
            dispatch({ type: actions.SET_PLAYER_SETTINGS, data: response });
        } finally {
            setIsSavingShowGameScore(false);
        }
    };
    const modalContainerRef = useRef();

    return (
        <>
            <StyledHeader>
                <Link to="/" className="h3">
                    <img src={header} alt={translate('common.site-name-portal')} className="header-logo" title={translate('common.site-name-portal')} />
                </Link>
                <div className="spacer" />
                {userPlayerId && (
                    <div className="d-flex align-items-center me-3">
                        <span>{FullName || loggedInUser.player?.FullName || loggedInUser.FullName}</span>
                    </div>
                )}
                {!userPlayerId && (
                    <>
                        <div className="d-flex align-items-center me-3">
                            <span>{translate('nav.logged-in-as')}:&nbsp;</span>
                            <PlayerSelector player={FullName} onSelect={onPlayerSelect} teamid={TeamID} isInAppSwitcher />
                        </div>
                        <div onClick={changeTeam}>
                            <Icon icon="user-plus" size="2em" style={{ marginRight: '15px' }} />
                        </div>
                    </>
                )}
                <Dropdown align="end">
                    <Dropdown.Toggle as={SupportToggle} />
                    <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setShowFeedbackModal(true)}>{translate('nav.submit-feedback')}</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Header>{translate('nav.view')}</Dropdown.Header>
                        {(!IsPitcher || !IsMlb) && (
                            <>
                                <Dropdown.Item className="subgroup-dropdown-item" onClick={false}>
                                    <div className="d-flex align-items-center">
                                        {translate('nav.game-score')}
                                        <StyledGameScoreToggleWrapper onClick={(e) => { e.stopPropagation(); }}>
                                            <Switch
                                                checked={ShowGameScore}
                                                disabled={isSavingShowGameScore}
                                                onChange={updateShowGameScore}
                                                onLabel=""
                                                offLabel=""
                                            />
                                        </StyledGameScoreToggleWrapper>
                                    </div>
                                </Dropdown.Item>
                            </>
                        )}
                        <Dropdown.Item className="subgroup-dropdown-item" onClick={() => setShowLanguageModal(true)}>{translate('nav.language-settings')}</Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={logout}>{translate('common.logout')}</Dropdown.Item>
                        {isDeveloper && <Dropdown.Header className="mt-2">{translate('nav.build')}: { buildName }</Dropdown.Header>}
                    </Dropdown.Menu>
                </Dropdown>
            </StyledHeader>
            <StyledContainer fluid {...rest}>
                {children}
            </StyledContainer>
            <StyledFooter>
                &copy; 2007-{(new Date()).getFullYear()} New York Yankees. {translate('common.copyright')}
            </StyledFooter>
            <div ref={modalContainerRef} />
            <FeedbackModal showModal={showFeedbackModal} setShowModal={setShowFeedbackModal} modalContainer={modalContainerRef} />
            <LanguageModal showModal={showLanguageModal} setShowModal={setShowLanguageModal} modalContainer={modalContainerRef} />
        </>
    );
};

SectionContentContainer.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.node,
        PropTypes.arrayOf(PropTypes.number),
    ]).isRequired,
};

export {
    SectionContentContainer as default,
    StyledHeader,
    StyledContainer,
};
